import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import useWorkspaceMembership from "../../../models/workspace-membership/use-workspace-membership";
import {usePermission} from "../../../system/usePermission";
import {useTranslate} from "../../../system/useTranslate";
import FreeBoard from "../../projects/tabs/free-board";
import {MyMeetingsStrip} from "../../projects/meetings/my-meetings-list";

function TasksBoard({tasks, updateTask, createTask, resetFiltersKey}) {
    const translate = useTranslate();
    const permission = usePermission();
    const workspaceApi = useWorkspaceMembership();
    const workspaceMembership = useSelector((state) => state.app.workspaceMembershipData);
    const canManageTasks = permission.can("canManageTasks");
    const savedTaskBoard = useMemo(
        () => workspaceMembership?.workspaceConfigs?.taskBoard || {},
        [workspaceMembership?.workspaceConfigs?.taskBoard],
    );
    // The board layout is saved as one object. Each change is stamped (savedAt) and kept locally, and the saves go out
    // one at a time with the latest layout, so a slower answer or a membership reload that started earlier can never put
    // an older layout back (cards jumping back after a move).
    const [localTaskBoard, setLocalTaskBoard] = useState(null);
    const taskBoardConfig = useMemo(
        () => (localTaskBoard && (localTaskBoard.savedAt || 0) >= (savedTaskBoard.savedAt || 0) ? localTaskBoard : savedTaskBoard),
        [localTaskBoard, savedTaskBoard],
    );
    const boardColumns = useMemo(() => {
        if (Array.isArray(taskBoardConfig?.columns) && taskBoardConfig.columns.length) {
            return taskBoardConfig.columns;
        }
        return [
            {id: "backlog", title: translate("free-board.column.backlog"), taskStatus: "todo", showSubtasks: true},
            {id: "in-progress", title: translate("free-board.column.in-progress"), taskStatus: "doing", showSubtasks: true},
            {id: "completed", title: translate("free-board.column.completed"), taskStatus: "done", showSubtasks: true},
        ];
    }, [taskBoardConfig?.columns, translate]);
    const taskBoardConfigRef = useRef(taskBoardConfig);
    const workspaceConfigsRef = useRef(workspaceMembership?.workspaceConfigs || {});
    const saveQueue = useRef(Promise.resolve());
    const updateWorkspaceConfigsRef = useRef(workspaceApi.updateWorkspaceConfigs);
    updateWorkspaceConfigsRef.current = workspaceApi.updateWorkspaceConfigs;
    useEffect(() => {
        // never step back to an older layout than the one already shown
        if ((taskBoardConfig.savedAt || 0) >= (taskBoardConfigRef.current?.savedAt || 0)) taskBoardConfigRef.current = taskBoardConfig;
        workspaceConfigsRef.current = {...(workspaceMembership?.workspaceConfigs || {}), taskBoard: taskBoardConfigRef.current};
    }, [taskBoardConfig, workspaceMembership?.workspaceConfigs]);

    const persistTaskBoardPatch = useCallback(async (patch) => {
        const nextTaskBoard = {...taskBoardConfigRef.current, ...patch, savedAt: Date.now()};
        taskBoardConfigRef.current = nextTaskBoard;
        workspaceConfigsRef.current = {...workspaceConfigsRef.current, taskBoard: nextTaskBoard};
        setLocalTaskBoard(nextTaskBoard);
        // one save at a time, each sending the newest layout at the moment it goes out
        const run = saveQueue.current.catch(() => null).then(() => updateWorkspaceConfigsRef.current({
            ...workspaceConfigsRef.current,
            taskBoard: taskBoardConfigRef.current,
        }));
        saveQueue.current = run;
        return run;
    }, []);

    const updateBoardColumns = useCallback(async (_membership, data) => (
        persistTaskBoardPatch({columns: data.taskBoardColumns})
    ), [persistTaskBoardPatch]);
    const persistTaskPlacements = useCallback(async (updates) => {
        const placements = {...(taskBoardConfigRef.current?.placements || {})};
        updates.forEach(({taskId, columnId, sortIndex}) => {
            if (!taskId) return;
            placements[String(taskId)] = {columnId: String(columnId), sortIndex};
        });
        return persistTaskBoardPatch({placements});
    }, [persistTaskBoardPatch]);
    const personalTasks = useMemo(() => {
        const placements = taskBoardConfig?.placements || {};
        return (tasks || []).map((task) => {
            const taskId = String(task?.documentId || task?.id || "");
            const placement = placements[taskId];
            return placement ? {
                ...task,
                taskBoardColumnId: placement.columnId,
                taskBoardSortIndex: placement.sortIndex,
            } : task;
        });
    }, [taskBoardConfig?.placements, tasks]);
    const boardAccess = useMemo(() => ({
        canCreateTask: canManageTasks,
        canChangeTaskStatus: canManageTasks,
        canUpdateTask: canManageTasks,
        canEditProject: canManageTasks,
    }), [canManageTasks]);

    return (
        <div className="flex flex-column h-full min-h-0">
            <MyMeetingsStrip/>
            <div className="flex-1 min-h-0">
        <FreeBoard
            tasks={personalTasks}
            updateTask={updateTask}
            createTask={createTask}
            boardColumns={boardColumns}
            sidebarBoardColumns={[]}
            projectData={{...workspaceMembership, taskBoardColumns: boardColumns}}
            updateProject={updateBoardColumns}
            columnStorageField="taskBoardColumns"
            columnIdField="taskBoardColumnId"
            sortIndexField="taskBoardSortIndex"
            persistPlacementOnTask={false}
            onPersistTaskPlacements={persistTaskPlacements}
            projectAccess={boardAccess}
            resetFiltersKey={resetFiltersKey}
        />
            </div>
        </div>
    );
}

export default TasksBoard;
